import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { saveBlob } from "../../lib/useOp";
import { Btn, Card } from "../kit/Kit";

const fmtV = (v) => (typeof v === "number" ? (Math.abs(v) < 1 && v !== 0 ? `${(v * 100).toFixed(2)}%` : v.toLocaleString("es-MX", { maximumFractionDigits: 2 })) : v);

export const LoanLevelTape = ({ ds }) => {
  const [d, setD] = useState(null);
  const [grupo, setGrupo] = useState(null);
  useEffect(() => { http.get("/lm/tape/loan-level", { params: { dataset: ds } }).then((r) => { setD(r.data); setGrupo((g) => g || r.data.diccionario[0]?.grupo); }).catch((e) => toast.error(errMsg(e))); }, [ds]);
  const grupos = useMemo(() => (d ? [...new Set(d.diccionario.map((x) => x.grupo))] : []), [d]);
  const cols = d ? d.diccionario.filter((x) => x.grupo === grupo).map((x) => x.campo) : [];
  const bajar = async (formato) => {
    try { const r = await http.get("/lm/tape/loan-level", { params: { dataset: ds, formato }, responseType: "blob" }); saveBlob(r.data, `loan_level_tape_${ds}.${formato}`); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return null;
  const err = d.validacion.filter((x) => x.severidad === "Error").length;
  return (
    <Card title="Loan-level tape" subtitle={`${d.resumen.campos} campos por crédito · ${d.resumen.creditos} créditos · "Sin dato" cuando no existe`} testid="ll-card">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {["xlsx", "csv"].map((f) => <Btn key={f} kind="secondary" onClick={() => bajar(f)} data-testid={`ll-${f}`}><Download size={14} />{f.toUpperCase()}</Btn>)}
        <span className="text-xs text-[#5C5850]" data-testid="ll-validacion-resumen">Validación: {err} errores · {d.validacion.length - err} advertencias</span>
      </div>
      <div className="mb-3 flex flex-wrap gap-1">
        {grupos.map((g) => (
          <button key={g} onClick={() => setGrupo(g)} data-testid={`ll-grupo-${g}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${g === grupo ? "border-[#111] bg-[#111] text-white" : "border-[#E2DDD5] text-[#5C5850] hover:bg-[#F2F0EC]"}`}>
            {g} · {Math.round((d.resumen.completitud[g] || 0) * 100)}%
          </button>))}
      </div>
      <div className="max-h-[28rem] overflow-auto rounded-xl border border-[#E2DDD5]" data-testid="ll-tabla">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-[#F7F5F1]"><tr><th className="px-2 py-2 text-left">Loan ID</th>{cols.filter((c) => c !== "Loan ID").map((c) => <th key={c} className="whitespace-nowrap px-2 py-2 text-left">{c}</th>)}</tr></thead>
          <tbody>{d.rows.map((r) => (
            <tr key={r["Loan ID"]} className="border-t border-[#EFEBE4]" data-testid={`ll-row-${r["Loan ID"]}`}>
              <td className="whitespace-nowrap px-2 py-1.5 font-semibold">{r["Loan ID"]}</td>
              {cols.filter((c) => c !== "Loan ID").map((c) => <td key={c} className={`whitespace-nowrap px-2 py-1.5 tabular-nums ${r[c] === "Sin dato" ? "text-[#B0A99C]" : ""}`}>{fmtV(r[c])}</td>)}
            </tr>))}</tbody>
        </table>
      </div>
      {d.validacion.length > 0 && (
        <details className="mt-4" data-testid="ll-validacion">
          <summary className="cursor-pointer text-sm font-semibold">Hallazgos de validación ({d.validacion.length})</summary>
          <ul className="mt-2 max-h-60 space-y-1 overflow-auto text-xs">{d.validacion.map((x, i) => <li key={i}><b className={x.severidad === "Error" ? "text-red-700" : "text-amber-700"}>{x.severidad}</b> · {x.regla} · {x.loan_id}: {x.detalle}</li>)}</ul>
        </details>)}
    </Card>
  );
};
